import {
  faCalendarCheck,
  faClockRotateLeft,
  faPlay,
  faScrewdriverWrench,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import dayjs from 'dayjs';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import CardSkeleton from 'src/components/maintenance/CardSkeleton';
import Button from 'src/components/ui/Button';
import EmptyState from 'src/components/ui/EmptyState';
import ListTable from 'src/components/ui/ListTable';
import ResourceTitleCard from 'src/components/ui/ResourceTitleCard';
import Switch from 'src/components/ui/Switch';
import { MAINTENANCE_EVENT_STATUS } from 'src/constants/maintenance';

import MaintenanceEventCard from './MaintenanceEventCard';

const activityTime = event =>
  dayjs(event.started_at || event.completed_at || event.created).valueOf();

const EventCardSkeleton = () => <CardSkeleton avatar={44} lines={['55%', '40%']} />;

/** Maintenance home — Start · Planned events · read-only Activity. */
const MaintenanceHome = ({
  title,
  subtitle,
  icon,
  titleHref,
  titleIcon,
  hasRequirements,
  requirementsLoading,
  loading,
  events,
  eventNamesByUri,
  fulfillmentsByEventUri,
  downtimesByUri,
  usersByUri,
  canLinkDowntime,
  onStart,
  onOpenPlanned,
  onResume,
  onDetails,
  onOpenActivity,
  instanceTargetUri,
}) => {
  const [plannedOpen, setPlannedOpen] = useState(true);
  const [activityOpen, setActivityOpen] = useState(true);
  const [showCancelled, setShowCancelled] = useState(false);

  const planned = events
    .filter(event => event.status === MAINTENANCE_EVENT_STATUS.PLANNED)
    .sort((a, b) => activityTime(a) - activityTime(b));

  const activity = events
    .filter(event => event.status !== MAINTENANCE_EVENT_STATUS.PLANNED)
    .filter(event => showCancelled || event.status !== MAINTENANCE_EVENT_STATUS.CANCELLED)
    .sort((a, b) => activityTime(b) - activityTime(a));

  const hasCancelled = events.some(event => event.status === MAINTENANCE_EVENT_STATUS.CANCELLED);

  return (
    <div className='mnt-enter'>
      <ResourceTitleCard
        icon={icon}
        title={title}
        titleHref={titleHref}
        titleIcon={titleIcon}
        subtitle={subtitle}
        monoSubtitle={false}
        instanceTargetUri={instanceTargetUri}
      />

      {requirementsLoading || hasRequirements ? (
        <Button
          fill
          block
          variant='primary'
          size='large'
          className='mnt-start-btn'
          disabled={requirementsLoading}
          icon={<FontAwesomeIcon icon={faPlay} />}
          onClick={onStart}
        >
          Start Maintenance Event
        </Button>
      ) : (
        <EmptyState icon={faScrewdriverWrench} title='No maintenance requirements yet'>
          Requirements for this equipment are configured by your administrator. Once they&rsquo;re
          added, you can start maintenance events here.
        </EmptyState>
      )}

      {planned.length > 0 && (
        <ListTable
          boxedIcon
          icon={faCalendarCheck}
          title='Planned'
          count={planned.length}
          isExpanded={plannedOpen}
          onToggle={() => setPlannedOpen(prev => !prev)}
        >
          <div className='mnt-section-body'>
            {planned.map(event => (
              <MaintenanceEventCard
                key={event.uri}
                event={event}
                name={eventNamesByUri[event.uri]}
                fulfillments={fulfillmentsByEventUri[event.uri] || []}
                downtime={event.downtime ? downtimesByUri[event.downtime] : null}
                usersByUri={usersByUri}
                canLinkDowntime={canLinkDowntime}
                onTap={() => onOpenPlanned(event)}
              />
            ))}
          </div>
        </ListTable>
      )}

      {(events.length > 0 || hasRequirements || requirementsLoading) && (
        <ListTable
          boxedIcon
          icon={faClockRotateLeft}
          title='Activity'
          count={activity.length}
          isExpanded={activityOpen}
          onToggle={() => setActivityOpen(prev => !prev)}
        >
          <div className='mnt-section-body'>
            {hasCancelled && (
              <div className='mnt-section-tools'>
                <Switch
                  id='mnt-show-cancelled'
                  label='Show Cancelled'
                  checked={showCancelled}
                  onChange={() => setShowCancelled(prev => !prev)}
                />
              </div>
            )}
            {loading ? (
              <>
                {Array.from({ length: 3 }).map((_, index) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <EventCardSkeleton key={index} />
                ))}
              </>
            ) : activity.length === 0 ? (
              <EmptyState variant='note'>
                No maintenance has been recorded on this equipment yet.
              </EmptyState>
            ) : (
              activity.map(event => {
                const isInProgress = event.status === MAINTENANCE_EVENT_STATUS.IN_PROGRESS;
                return (
                  <MaintenanceEventCard
                    key={event.uri}
                    event={event}
                    name={eventNamesByUri[event.uri]}
                    fulfillments={fulfillmentsByEventUri[event.uri] || []}
                    downtime={event.downtime ? downtimesByUri[event.downtime] : null}
                    usersByUri={usersByUri}
                    canLinkDowntime={canLinkDowntime}
                    onTap={isInProgress ? null : () => onOpenActivity(event)}
                    onResume={isInProgress ? () => onResume(event) : null}
                    onDetails={isInProgress ? () => onDetails(event) : null}
                  />
                );
              })
            )}
          </div>
        </ListTable>
      )}
    </div>
  );
};

MaintenanceHome.propTypes = {
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string,
  icon: PropTypes.object.isRequired,
  titleHref: PropTypes.string,
  titleIcon: PropTypes.object,
  events: PropTypes.array.isRequired,
  eventNamesByUri: PropTypes.object,
  fulfillmentsByEventUri: PropTypes.object,
  downtimesByUri: PropTypes.object,
  usersByUri: PropTypes.object,
  canLinkDowntime: PropTypes.bool,
  hasRequirements: PropTypes.bool,
  requirementsLoading: PropTypes.bool,
  loading: PropTypes.bool,
  onStart: PropTypes.func.isRequired,
  onOpenPlanned: PropTypes.func.isRequired,
  onResume: PropTypes.func.isRequired,
  onDetails: PropTypes.func.isRequired,
  onOpenActivity: PropTypes.func.isRequired,
  instanceTargetUri: PropTypes.string.isRequired,
};

MaintenanceHome.defaultProps = {
  canLinkDowntime: true,
  hasRequirements: false,
  requirementsLoading: false,
  loading: false,
  titleHref: null,
  titleIcon: null,
  subtitle: null,
  eventNamesByUri: {},
  fulfillmentsByEventUri: {},
  downtimesByUri: {},
  usersByUri: {},
};

export default MaintenanceHome;
